import {
  Badge,
  Button,
  buttonVariants,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
  EmptyState,
  ErrorState,
  Field,
  FieldLabel,
  Input,
  Skeleton,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  useToast,
} from "@forge/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ExternalLink,
  Link2,
  MessageCircleQuestion,
  Pause,
  Pencil,
  Play,
  Tags,
} from "lucide-react";
import { useState } from "react";
import { CopyButton } from "../../components/copy-button";
import { QuestionThread } from "../../components/question-thread";
import { PageHeader } from "../../components/page-header";
import { api, unwrap } from "../../lib/api";
import { secureAssetUrl } from "../../lib/url";
import {
  listingSku,
  openOnChannelLabel,
  skuOnChannelLabel,
  listingStockLabel,
} from "../../lib/channel";
import {
  formatBRL,
  formatDateTime,
  formatPercent,
  parseBRL,
} from "../../lib/format";
import { suggestReprice } from "../../lib/reprice";
import { skuLinkClassName } from "../../lib/stock";
import { targetPriceForMargin } from "../../lib/target-price";
import {
  LISTING_STATUS_LABELS,
  LISTING_STATUS_VARIANTS,
  LOGISTIC_TYPE_LABELS,
} from "../../lib/order-status";

export const Route = createFileRoute("/_app/listings_/$id")({
  component: ListingDetailPage,
});

/**
 * Mirrors the real layout so the page never jumps: a centered spinner parks the
 * eye in the middle of the viewport and then drops the content at the top.
 */
function ListingDetailSkeleton() {
  return (
    <>
      <div className="mb-4 flex flex-col gap-3 md:mb-5 md:flex-row md:items-end md:justify-between">
        <div className="flex flex-col gap-1.5">
          <Skeleton className="h-2.5 w-16" />
          <Skeleton className="h-4 w-72 max-w-full" />
          <Skeleton className="h-2.5 w-56 max-w-full" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-7 w-20" />
          <Skeleton className="h-7 w-28" />
        </div>
      </div>
      <div className="flex flex-col gap-3">
        <Card>
          <CardContent className="flex flex-col gap-4 p-4 sm:flex-row sm:items-start">
            <Skeleton className="size-16 shrink-0" />
            <div className="grid min-w-0 flex-1 grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
              {Array.from({ length: 4 }, (_, index) => (
                <div key={index} className="flex flex-col gap-1.5">
                  <Skeleton className="h-2 w-16" />
                  <Skeleton className="h-3.5 w-20" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        {["h-8", "h-6"].map((bodyHeight) => (
          <Card key={bodyHeight}>
            <CardContent className="flex flex-col gap-3 p-4">
              <Skeleton className="h-3 w-24" />
              <Skeleton className={cn("w-full", bodyHeight)} />
              <Skeleton className="h-3.5 w-2/3" />
            </CardContent>
          </Card>
        ))}
      </div>
    </>
  );
}

function ListingDetailPage() {
  const { id } = Route.useParams();
  const queryClient = useQueryClient();
  const listingQuery = useQuery({
    queryKey: ["listings", id],
    queryFn: async () => unwrap(await api.listings({ id }).get()),
  });

  if (listingQuery.isLoading) {
    return <ListingDetailSkeleton />;
  }

  // Before the not-found branch on purpose: a failed request is not an absent
  // listing, and saying so sends the user looking for a problem that isn't there.
  if (listingQuery.isError) {
    return (
      <Card>
        <ErrorState
          title="Não foi possível carregar o anúncio"
          error={listingQuery.error}
          retrying={listingQuery.isFetching}
          onRetry={() => void listingQuery.refetch()}
        />
      </Card>
    );
  }

  const data =
    listingQuery.data && "listing" in listingQuery.data
      ? listingQuery.data
      : null;

  if (!data) {
    return (
      <Card>
        <EmptyState
          icon={<Tags />}
          title="Anúncio não encontrado"
          description="Importe os anúncios da conta em Conexões ou volte à lista."
          action={
            <Link
              to="/listings"
              className={buttonVariants({ variant: "secondary" })}
            >
              Voltar aos anúncios
            </Link>
          }
        />
      </Card>
    );
  }

  const { listing, mappings } = data;
  const questions = "questions" in data ? data.questions : [];
  const promotions = "promotions" in data ? data.promotions : [];
  const writesEnabled =
    "writesEnabled" in data ? data.writesEnabled : false;
  const health = "health" in listing ? listing.health : null;
  const buyBox = "buyBox" in listing ? listing.buyBox : null;
  const canWrite =
    writesEnabled &&
    (listing.status === "active" || listing.status === "paused");
  const logisticLabel = listing.logisticType
    ? (LOGISTIC_TYPE_LABELS[listing.logisticType] ?? listing.logisticType)
    : null;
  const provider =
    "accountProvider" in listing ? listing.accountProvider : "mercadolivre";
  const sku = listingSku(listing);

  return (
    <>
      <PageHeader
        parent={
          <Link
            to="/listings"
            className="text-2xs font-medium text-ink-muted transition-colors duration-100 hover:text-ink"
          >
            Anúncios
          </Link>
        }
        title={listing.title}
        description={`${listing.accountNickname} · ${listing.externalItemId} · ${skuOnChannelLabel(provider)}: ${sku ?? "não informado"}`}
        actions={
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <Badge
              variant={LISTING_STATUS_VARIANTS[listing.status] ?? "neutral"}
              dot
            >
              {LISTING_STATUS_LABELS[listing.status] ?? listing.status}
            </Badge>
            {listing.logisticType === "fulfillment" && (
              <Tooltip content="Estoque gerenciado pelo canal — o Bridge não empurra saldo neste anúncio">
                <Badge variant="accent">Full</Badge>
              </Tooltip>
            )}
            <ListingStatusToggle
              listingId={id}
              status={listing.status}
              canWrite={canWrite}
              writesEnabled={writesEnabled}
            />
            {listing.permalink && (
              <a
                href={listing.permalink}
                target="_blank"
                rel="noreferrer"
                className={buttonVariants({ variant: "secondary", size: "sm" })}
              >
                <ExternalLink />
                {openOnChannelLabel(provider)}
              </a>
            )}
          </div>
        }
      />

      {/* Cards carry `animate-rise` individually; the cascade lives here so the
          page reads as a sequence instead of one wall of movement. */}
      <div className="flex flex-col gap-3 [&>*:nth-child(2)]:[animation-delay:40ms] [&>*:nth-child(3)]:[animation-delay:80ms] [&>*:nth-child(4)]:[animation-delay:120ms] [&>*:nth-child(n+5)]:[animation-delay:160ms]">
        <Card className="animate-rise">
          <CardContent className="flex flex-col gap-4 p-4 sm:flex-row sm:items-start">
            {secureAssetUrl(listing.thumbnailUrl) && (
              <img
                src={secureAssetUrl(listing.thumbnailUrl)!}
                alt=""
                className="size-16 shrink-0 rounded-control border border-line object-cover"
              />
            )}
            <div className="grid min-w-0 flex-1 grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
              <MetaRow
                label={skuOnChannelLabel(provider)}
                value={sku ?? "Não informado"}
                mono
                copy={Boolean(sku)}
              />
              <ListingPriceEditor
                listingId={id}
                price={listing.price}
                canWrite={canWrite}
              />
              <MetaRow
                label={listingStockLabel(listing.logisticType, provider)}
                value={String(listing.availableQuantity)}
                mono
              />
              {logisticLabel && (
                <MetaRow label="Logística" value={logisticLabel} />
              )}
              {"product" in listing && listing.product?.margin != null && (
                <MetaRow
                  label="Margem de produto"
                  value={`${formatPercent(listing.product.margin)}${
                    listing.product.profit
                      ? ` · ${formatBRL(listing.product.profit)}`
                      : ""
                  }`}
                  mono
                />
              )}
              {"demand" in listing && listing.demand?.visits30d != null && (
                <MetaRow
                  label="Visitas 30d"
                  value={String(listing.demand.visits30d)}
                  mono
                />
              )}
              {"demand" in listing && listing.demand?.conversion != null && (
                <MetaRow
                  label="Conversão 30d"
                  value={`${formatPercent(listing.demand.conversion)}${
                    listing.demand.sold30d != null
                      ? ` · ${listing.demand.sold30d} vendas`
                      : ""
                  }${listing.demand.dropped ? " · caiu" : ""}`}
                  mono
                />
              )}
            </div>
          </CardContent>
        </Card>

        {health ? (
          <ListingHealthCard
            listingId={id}
            writesEnabled={writesEnabled}
            status={listing.status}
            health={health}
            reviewAverage={
              "reviewAverage" in listing ? listing.reviewAverage : null
            }
            reviewCount={"reviewCount" in listing ? listing.reviewCount : null}
            mlLevel={"mlLevel" in listing ? listing.mlLevel : null}
            permalink={listing.permalink}
          />
        ) : null}

        <section className="flex flex-col gap-3">
          <p className="text-2xs font-medium uppercase tracking-wider text-ink-muted">
            Concorrência
          </p>
          {buyBox ? <ListingBuyBoxCard buyBox={buyBox} /> : null}
          <ListingDemandCard
            listingId={id}
            categoryId={"categoryId" in listing ? listing.categoryId : null}
            accountId={"accountId" in listing ? listing.accountId : undefined}
          />
        </section>

        {promotions.length > 0 ? (
          <ListingPromotionsCard
            listingId={id}
            promotions={promotions}
            writesEnabled={writesEnabled}
          />
        ) : null}

        {"pricing" in data && data.pricing && (
          <TargetPriceCard
            listingId={id}
            currentPrice={listing.price}
            canWrite={canWrite}
            pricing={data.pricing}
            buyBox={buyBox}
            suggestion={"suggestion" in data ? data.suggestion : null}
          />
        )}

        <Card>
          <CardHeader>
            <CardTitle>Vínculo de estoque</CardTitle>
          </CardHeader>
          {mappings.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>SKU</TableHead>
                  <TableHead>Depósito</TableHead>
                  <TableHead className="text-right">Consome</TableHead>
                  <TableHead className="text-right">Custo</TableHead>
                  <TableHead>Variação</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mappings.map((mapping) => (
                  <TableRow key={mapping.id}>
                    <TableCell>
                      {"skuId" in mapping && mapping.skuId ? (
                        <Link
                          to="/stock/$id"
                          params={{ id: mapping.skuId }}
                          className={skuLinkClassName}
                        >
                          {mapping.skuCode}
                        </Link>
                      ) : (
                        <p className="font-mono text-ink">{mapping.skuCode}</p>
                      )}
                      <p className="text-2xs text-ink-muted">
                        {mapping.skuTitle}
                      </p>
                    </TableCell>
                    <TableCell>{mapping.warehouseName}</TableCell>
                    <TableCell className="text-right font-mono">
                      {mapping.quantity}
                    </TableCell>
                    <TableCell className="text-right font-mono text-2xs">
                      {"skuCost" in mapping && mapping.skuCost != null
                        ? formatBRL(mapping.skuCost)
                        : "—"}
                    </TableCell>
                    <TableCell className="font-mono text-2xs text-ink-muted">
                      {mapping.externalVariationId ?? "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <EmptyState
              icon={<Link2 />}
              title="Anúncio sem vínculo"
              description="Use Vincular pelo SKU do anúncio em Anúncios se o código já existe no canal, ou vincule na mão em Estoque."
              action={
                <Link
                  to="/stock"
                  className={buttonVariants({ variant: "secondary", size: "sm" })}
                >
                  Ir para Estoque
                </Link>
              }
            />
          )}
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-1.5">
              <MessageCircleQuestion className="size-3.5 text-ink-muted" />
              Perguntas
            </CardTitle>
          </CardHeader>
          {questions.length > 0 ? (
            <div>
              {questions.map((question) => (
                <QuestionThread
                  key={question.id}
                  question={question}
                  writesEnabled={writesEnabled}
                  onAnswered={() => {
                    void queryClient.invalidateQueries({
                      queryKey: ["listings", id],
                    });
                    void queryClient.invalidateQueries({
                      queryKey: ["questions"],
                    });
                  }}
                />
              ))}
            </div>
          ) : (
            <EmptyState
              icon={<MessageCircleQuestion />}
              title="Nenhuma pergunta neste anúncio"
              description="Novas perguntas do Mercado Livre aparecem aqui e na caixa unificada."
              action={
                <Link
                  to="/questions"
                  search={{ page: 1 }}
                  className={buttonVariants({ variant: "secondary", size: "sm" })}
                >
                  Ver todas as perguntas
                </Link>
              }
            />
          )}
        </Card>

        <p className="text-2xs text-ink-muted">
          Sincronizado{" "}
          {listing.syncedAt ? formatDateTime(listing.syncedAt) : "—"}
        </p>
      </div>
    </>
  );
}

function MetaRow({
  label,
  value,
  mono,
  copy,
}: {
  label: string;
  value: string;
  mono?: boolean;
  copy?: boolean;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="text-2xs font-medium text-ink-muted">{label}</span>
      <span className="flex min-w-0 items-center gap-1">
        <span
          className={
            mono
              ? "truncate font-mono text-[0.8125rem] text-ink"
              : "truncate text-[0.8125rem] text-ink"
          }
          title={value}
        >
          {value}
        </span>
        {copy && <CopyButton value={value} label={`Copiar ${label.toLowerCase()}`} />}
      </span>
    </div>
  );
}

function ListingStatusToggle({
  listingId,
  status,
  canWrite,
  writesEnabled,
}: {
  listingId: string;
  status: string;
  canWrite: boolean;
  writesEnabled: boolean;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const next = status === "paused" ? "active" : "paused";
  const toggle = useMutation({
    mutationFn: async () =>
      unwrap(await api.listings({ id: listingId }).patch({ status: next })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["listings"] });
      toast.add({
        type: "success",
        title: next === "paused" ? "Anúncio pausado" : "Anúncio ativado",
      });
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title: "Não foi possível alterar o status",
        description:
          error instanceof Error ? error.message : "Tente novamente.",
      });
    },
  });

  const label = status === "paused" ? "Ativar" : "Pausar";
  const button = (
    <Button
      variant="secondary"
      size="sm"
      disabled={!canWrite}
      loading={toggle.isPending}
      onClick={() => toggle.mutate()}
    >
      {status === "paused" ? <Play /> : <Pause />}
      {label}
    </Button>
  );

  if (canWrite) return button;
  return (
    <Tooltip
      content={
        writesEnabled
          ? "Só anúncios ativos ou pausados podem mudar de status"
          : "Escrita desligada nesta organização"
      }
    >
      <span className="inline-flex">{button}</span>
    </Tooltip>
  );
}

const BUY_BOX_LABEL: Record<string, string> = {
  winning: "Você ganha o card",
  sharing_first_place: "Dividindo o topo",
  losing: "Outro vendedor ganha",
  listed: "No catálogo, sem o card",
};

const ML_LEVEL_LABEL: Record<string, string> = {
  low: "Baixa",
  medium: "Padrão",
  high: "Alta",
  estandar: "Padrão",
  estándar: "Padrão",
  profesional: "Profissional",
  professional: "Profissional",
};

function titleCase(value: string) {
  return value
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function mlLevelLabel(level: string | null | undefined) {
  if (!level) return null;
  return ML_LEVEL_LABEL[level.toLowerCase()] ?? titleCase(level);
}

function ListingDemandCard({
  listingId,
  categoryId,
  accountId,
}: {
  listingId: string;
  categoryId: string | null | undefined;
  accountId?: string;
}) {
  const demand = useQuery({
    queryKey: ["listings", listingId, "demand"],
    enabled: Boolean(categoryId),
    queryFn: async () =>
      unwrap(await api.listings({ id: listingId }).demand.get()),
  });
  const data = demand.data;
  const searches =
    data && "searches" in data && data.searches
      ? data.searches
      : { matched: [] as string[], missing: [] as string[] };
  // Holds the slot instead of returning null: this card resolves after the page
  // has settled, and appearing from nothing shoves everything below it down.
  if (categoryId && demand.isLoading) {
    return (
      <Card className="animate-rise">
        <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex flex-col gap-1.5">
            <Skeleton className="h-2 w-16" />
            <Skeleton className="h-7 w-20" />
            <Skeleton className="h-2.5 w-48 max-w-full" />
          </div>
          <Skeleton className="h-7 w-24" />
        </CardContent>
      </Card>
    );
  }
  if (!data || data.missingCategory || !categoryId) {
    return (
      <Card>
        <CardContent className="p-4">
          <p className="text-2xs font-medium uppercase tracking-wider text-ink-muted">
            Ranking da categoria
          </p>
          <p className="mt-0.5 text-2xs text-ink-muted">
            Reimporte os anúncios em Conexões para gravar a categoria.
          </p>
        </CardContent>
      </Card>
    );
  }
  const wantedCount = data.trends.wanted.length;
  const hero =
    data.own?.position != null
      ? `#${data.own.position} de 20`
      : "Fora dos 20 mais vendidos";
  const missing = searches.missing.slice(0, 3);
  return (
    <Card className="animate-rise">
      <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-2xs font-medium uppercase tracking-wider text-ink-muted">
            Ranking da categoria
          </p>
          <p
            className={cn(
              "mt-0.5 font-mono text-2xl font-medium tracking-tight",
              data.own ? "text-ink" : "text-danger",
            )}
          >
            {hero}
          </p>
          <p className="mt-0.5 text-2xs text-ink-muted">
            Posição entre os mais vendidos desta categoria
          </p>
          {wantedCount > 0 ? (
            <p className="mt-1.5 text-2xs text-ink-secondary">
              Seu título cobre {searches.matched.length} das {wantedCount}{" "}
              buscas mais feitas
              {missing.length > 0 ? ` · Não cobre: ${missing.join(", ")}` : ""}
            </p>
          ) : data.trends.growing.length > 0 ? (
            <p className="mt-1.5 text-2xs text-ink-muted">
              Subindo:{" "}
              {data.trends.growing
                .slice(0, 3)
                .map((row) => row.keyword)
                .join(" · ")}
            </p>
          ) : (
            <p className="mt-1.5 text-2xs text-ink-muted">
              {data.categoryName ?? "Ranking oficial da categoria"}
            </p>
          )}
        </div>
        <Link
          to="/demand"
          search={{
            accountId,
            categoryId: data.categoryId ?? undefined,
          }}
          className={buttonVariants({ variant: "secondary", size: "sm" })}
        >
          Ver categoria
        </Link>
      </CardContent>
    </Card>
  );
}

function ListingBuyBoxCard({
  buyBox,
}: {
  buyBox: {
    status: string;
    visitShare: number | null;
    sharing: number | null;
    priceToWin: number | null;
    winnerPrice: number | null;
    winnerItemId: string | null;
    lost: boolean;
  };
}) {
  const meta = [
    buyBox.visitShare != null
      ? `${formatPercent(buyBox.visitShare)} das visitas do produto`
      : null,
    buyBox.sharing != null && buyBox.sharing > 1
      ? `${buyBox.sharing} vendedores no topo`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Card className="animate-rise">
      <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-2xs font-medium uppercase tracking-wider text-ink-muted">
            Card do catálogo
          </p>
          <p
            className={cn(
              "mt-0.5 font-mono text-2xl font-medium tracking-tight",
              buyBox.lost ? "text-danger" : "text-ink",
            )}
          >
            {BUY_BOX_LABEL[buyBox.status] ?? buyBox.status}
          </p>
          <p className="mt-0.5 text-2xs text-ink-muted">
            Disputa com quem vende este mesmo produto
          </p>
          {meta ? <p className="mt-1 text-2xs text-ink-secondary">{meta}</p> : null}
        </div>
        <div className="min-w-0 sm:text-right">
          {buyBox.winnerPrice != null ? (
            <>
              <p className="text-2xs font-medium text-ink-muted">
                {buyBox.lost ? "Vencedor" : "Preço no card"}
              </p>
              <p
                className={cn(
                  "mt-0.5 font-mono text-xl font-medium tracking-tight",
                  buyBox.lost ? "text-danger" : "text-ink",
                )}
              >
                {formatBRL(buyBox.winnerPrice)}
              </p>
            </>
          ) : buyBox.priceToWin != null && buyBox.lost ? (
            <>
              <p className="text-2xs font-medium text-ink-muted">
                Preço para ganhar
              </p>
              <p className="mt-0.5 font-mono text-xl font-medium tracking-tight text-ink">
                {formatBRL(buyBox.priceToWin)}
              </p>
            </>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

const HEALTH_NEXT: Record<
  string,
  { label: string; to?: "/stock" | "/purchases" | "/claims" | "/listings" }
> = {
  unmapped: { label: "Vincular SKU", to: "/stock" },
  no_cost: { label: "Cadastrar custo", to: "/stock" },
  low_margin: { label: "Ajustar no preço-alvo abaixo" },
  no_visits: { label: "Conferir exposição no Mercado Livre" },
  zero_stock: { label: "Repor estoque", to: "/purchases" },
  ml_warning: { label: "Corrigir no Mercado Livre" },
  low_review: { label: "Ver avaliações no anúncio" },
  reputation: { label: "Resolver reclamações", to: "/claims" },
  promo_margin: { label: "Revisar campanha abaixo" },
  stock_drift: { label: "Reenviar estoque" },
};

/** What to do about a check the score could not run. */
const HEALTH_UNKNOWN_NEXT: Record<
  string,
  { hint: string; label?: string; to?: "/connections" }
> = {
  visits: {
    hint: "Sem importar visitas, não dá para dizer se o anúncio tem tráfego.",
    label: "Importar visitas",
    to: "/connections",
  },
  insights: {
    hint: "Score, advertências e avaliações do Mercado Livre estão desatualizados.",
    label: "Importar visitas",
    to: "/connections",
  },
  fees: {
    hint: "A margem acima é bruta: sem vendas recentes deste anúncio não dá para descontar comissão e frete.",
  },
};

function ListingHealthCard({
  listingId,
  writesEnabled,
  status,
  health,
  reviewAverage,
  reviewCount,
  mlLevel,
  permalink,
}: {
  listingId: string;
  writesEnabled: boolean;
  status: string;
  health: {
    score: number;
    sick: boolean;
    summary: string;
    issues: Array<{ id: string; label: string }>;
    unknowns?: Array<{ id: string; label: string }>;
    marginBasis?: "net" | "gross" | null;
    targetMargin?: number;
    visitsSyncedAt?: string | Date | null;
    mlScore: number | null;
    drift?: {
      forge: number | null;
      channel: number;
      lastError: string | null;
    } | null;
  };
  reviewAverage: number | null | undefined;
  reviewCount: number | null | undefined;
  mlLevel: string | null | undefined;
  permalink: string | null;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const pushStock = useMutation({
    mutationFn: async () =>
      unwrap(await api.listings({ id: listingId }).stock.post()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["listings"] });
      toast.add({ type: "success", title: "Estoque reenviado ao Mercado Livre" });
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title: "Não foi possível reenviar o estoque",
        description:
          error instanceof Error ? error.message : "Tente novamente.",
      });
    },
  });
  return (
    <Card className="animate-rise">
      <CardContent className="flex flex-col gap-4 p-4 sm:flex-row sm:items-start">
        <div className="shrink-0">
          <p className="text-2xs font-medium uppercase tracking-wider text-ink-muted">
            Saúde
          </p>
          <p
            className={cn(
              "mt-0.5 font-mono text-2xl font-medium tracking-tight",
              health.sick ? "text-danger" : "text-ink",
            )}
          >
            {health.score}
          </p>
          <p className="mt-0.5 text-2xs text-ink-muted">{health.summary}</p>
        </div>
        <div className="min-w-0 flex-1">
          {(() => {
            const level = mlLevelLabel(mlLevel);
            const reviews =
              reviewCount != null && reviewCount > 0 && reviewAverage != null
                ? `${reviewAverage.toLocaleString("pt-BR", {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  })}★ (${reviewCount} avaliaç${reviewCount === 1 ? "ão" : "ões"})`
                : null;
            const parts = [
              health.mlScore != null ? `Qualidade ML ${health.mlScore}/100` : null,
              level,
              reviews,
            ].filter(Boolean);
            return parts.length > 0 ? (
              <p className="mb-3 text-2xs text-ink-muted">{parts.join(" · ")}</p>
            ) : null;
          })()}
          {status === "closed" ? (
            <p className="text-[0.8125rem] text-ink-secondary">
              Anúncio finalizado — os indicadores abaixo são o último estado
              conhecido.
            </p>
          ) : health.issues.length === 0 ? (
            <p className="text-[0.8125rem] text-ink-secondary">
              Nada pendente neste anúncio.
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {health.issues.map((issue) => {
                const next = HEALTH_NEXT[issue.id];
                const openMl =
                  (issue.id === "ml_warning" ||
                    issue.id === "no_visits" ||
                    issue.id === "low_review") &&
                  permalink;
                return (
                  <li
                    key={issue.id}
                    className="flex items-start justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <p className="text-[0.8125rem] font-medium text-ink">
                        {issue.label}
                      </p>
                      {issue.id === "stock_drift" && health.drift ? (
                        <p className="mt-0.5 text-2xs text-ink-muted">
                          Bridge {health.drift.forge ?? "—"} · Mercado Livre{" "}
                          {health.drift.channel}
                          {health.drift.lastError
                            ? ` · ${health.drift.lastError}`
                            : null}
                        </p>
                      ) : null}
                      {issue.id === "low_margin" &&
                      health.targetMargin != null ? (
                        <p className="mt-0.5 text-2xs text-ink-muted">
                          Alvo {formatPercent(health.targetMargin)} ·{" "}
                          {health.marginBasis === "net"
                            ? "já com comissão e frete"
                            : "margem bruta, sem comissão e frete"}
                        </p>
                      ) : null}
                    </div>
                    {issue.id === "stock_drift" ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={!writesEnabled}
                        loading={pushStock.isPending}
                        onClick={() => pushStock.mutate()}
                      >
                        {next?.label ?? "Reenviar estoque"}
                      </Button>
                    ) : openMl ? (
                      <a
                        href={permalink}
                        target="_blank"
                        rel="noreferrer"
                        className="shrink-0 text-2xs font-medium text-ember-strong hover:underline"
                      >
                        {next?.label ?? "Abrir no ML"} →
                      </a>
                    ) : next?.to ? (
                      <Link
                        to={next.to}
                        className="shrink-0 text-2xs font-medium text-ember-strong hover:underline"
                      >
                        {next.label} →
                      </Link>
                    ) : next ? (
                      <span className="shrink-0 text-2xs text-ink-muted">
                        {next.label}
                      </span>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
          {status !== "closed" && health.unknowns && health.unknowns.length > 0 ? (
            <div className="mt-3 border-t border-line pt-3">
              <p className="text-2xs font-medium uppercase tracking-wider text-ink-muted">
                Fora do alcance do score
              </p>
              <ul className="mt-1.5 flex flex-col gap-1.5">
                {health.unknowns.map((unknown) => {
                  const next = HEALTH_UNKNOWN_NEXT[unknown.id];
                  return (
                    <li
                      key={unknown.id}
                      className="flex items-start justify-between gap-3"
                    >
                      <div className="min-w-0">
                        <p className="text-2xs font-medium text-ink-secondary">
                          {unknown.label}
                        </p>
                        {next?.hint ? (
                          <p className="mt-0.5 text-2xs text-ink-muted">
                            {next.hint}
                          </p>
                        ) : null}
                      </div>
                      {next?.to ? (
                        <Link
                          to={next.to}
                          className="shrink-0 text-2xs font-medium text-ember-strong hover:underline"
                        >
                          {next.label} →
                        </Link>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

const PROMO_TYPE_LABEL: Record<string, string> = {
  DEAL: "Oferta",
  LIGHTNING: "Relâmpago",
  DOD: "Oferta do dia",
  VOLUME: "Volume",
  SMART: "Smart",
  PRICE_MATCHING: "Preço da concorrência",
};

const PROMO_STATUS_LABEL: Record<string, string> = {
  started: "Na campanha",
  candidate: "Elegível",
  pending: "Pendente",
};

function ListingPromotionsCard({
  listingId,
  promotions,
  writesEnabled,
}: {
  listingId: string;
  promotions: Array<{
    id: string | null;
    type: string;
    name: string | null;
    status: string;
    dealPrice: number | null;
    currentMargin: number | null;
    dealMargin: number | null;
    eatsMargin: boolean;
  }>;
  writesEnabled: boolean;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const join = useMutation({
    mutationFn: async (promo: (typeof promotions)[number]) => {
      if (!promo.id) throw new Error("Campanha sem identificador");
      return unwrap(
        await api.listings({ id: listingId }).promotions.post({
          promotionId: promo.id,
          promotionType: promo.type,
          dealPrice: promo.dealPrice ?? undefined,
        }),
      );
    },
    onMutate: (promo) => setBusyKey(`join:${promo.id ?? promo.type}`),
    onSettled: () => setBusyKey(null),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["listings", listingId] });
      void queryClient.invalidateQueries({ queryKey: ["cockpit"] });
      toast.add({ type: "success", title: "Entrou na campanha" });
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title: "Não foi possível entrar na campanha",
        description:
          error instanceof Error ? error.message : "Tente novamente.",
      });
    },
  });

  const leave = useMutation({
    mutationFn: async (promo: (typeof promotions)[number]) => {
      return unwrap(
        await api.listings({ id: listingId }).promotions.delete(undefined, {
          query: {
            promotionType: promo.type,
            promotionId: promo.id ?? undefined,
          },
        }),
      );
    },
    onMutate: (promo) => setBusyKey(`leave:${promo.id ?? promo.type}`),
    onSettled: () => setBusyKey(null),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["listings", listingId] });
      void queryClient.invalidateQueries({ queryKey: ["cockpit"] });
      toast.add({ type: "success", title: "Saiu da campanha" });
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title: "Não foi possível sair da campanha",
        description:
          error instanceof Error ? error.message : "Tente novamente.",
      });
    },
  });

  return (
    <Card className="animate-rise">
      <CardHeader>
        <CardTitle>Campanhas</CardTitle>
      </CardHeader>
      <ul>
        {promotions.map((promo, index) => {
          const key = `${promo.id ?? "none"}:${promo.type}:${index}`;
          const joining = busyKey === `join:${promo.id ?? promo.type}`;
          const leaving = busyKey === `leave:${promo.id ?? promo.type}`;
          const canJoin =
            writesEnabled &&
            promo.id != null &&
            (promo.status === "candidate" || promo.status === "pending");
          const canLeave =
            writesEnabled && promo.status === "started";
          return (
            <li
              key={key}
              className={cn(
                "flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between",
                index < promotions.length - 1 ? "border-b border-line" : "",
              )}
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-1.5">
                  <p className="text-[0.8125rem] font-medium text-ink">
                    {promo.name ?? PROMO_TYPE_LABEL[promo.type] ?? promo.type}
                  </p>
                  <Badge variant={promo.eatsMargin ? "danger" : "neutral"}>
                    {PROMO_STATUS_LABEL[promo.status] ?? promo.status}
                  </Badge>
                </div>
                {promo.currentMargin != null && promo.dealMargin != null ? (
                  <p
                    className={cn(
                      "mt-1 font-mono text-xl font-medium tracking-tight",
                      promo.eatsMargin ? "text-danger" : "text-ink",
                    )}
                  >
                    {formatPercent(promo.currentMargin)}
                    <span className="mx-1.5 text-ink-faint">→</span>
                    {formatPercent(promo.dealMargin)}
                  </p>
                ) : promo.dealPrice != null ? (
                  <p className="mt-1 font-mono text-[0.8125rem] text-ink">
                    {formatBRL(promo.dealPrice)}
                  </p>
                ) : (
                  <p className="mt-1 text-2xs text-ink-muted">
                    Sem preço de campanha ainda
                  </p>
                )}
                {promo.eatsMargin ? (
                  <p className="mt-0.5 text-2xs text-danger">
                    Esta campanha come a margem
                  </p>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                {canJoin ? (
                  <Button
                    size="sm"
                    disabled={join.isPending || leave.isPending}
                    loading={joining}
                    onClick={() => join.mutate(promo)}
                  >
                    Entrar
                  </Button>
                ) : null}
                {canLeave ? (
                  <Button
                    variant={promo.eatsMargin ? "danger" : "secondary"}
                    size="sm"
                    disabled={join.isPending || leave.isPending}
                    loading={leaving}
                    onClick={() => leave.mutate(promo)}
                  >
                    Sair
                  </Button>
                ) : null}
                {!writesEnabled &&
                (promo.status === "started" ||
                  promo.status === "candidate") ? (
                  <Tooltip content="Escrita desligada nesta organização">
                    <span className="text-2xs text-ink-muted">
                      {promo.status === "started" ? "Sair" : "Entrar"} bloqueado
                    </span>
                  </Tooltip>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function TargetPriceCard({
  listingId,
  currentPrice,
  canWrite,
  pricing,
  buyBox,
  suggestion,
}: {
  listingId: string;
  currentPrice: string;
  canWrite: boolean;
  pricing: {
    cost: string | null;
    feeRate: number | null;
    shipping: string | null;
    sampleOrders: number;
    source?: "sales" | "official" | null;
  };
  buyBox: {
    status: string;
    winnerPrice: number | null;
    priceToWin: number | null;
    lost: boolean;
  } | null;
  suggestion: {
    price: number | null;
    lowest: number | null;
    status: string | null;
  } | null;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [marginPct, setMarginPct] = useState("30");
  const parsedPct = Number(marginPct.replace(",", "."));
  const targetMargin =
    Number.isFinite(parsedPct) && parsedPct > 0 ? parsedPct / 100 : NaN;
  const result = targetPriceForMargin({
    cost: pricing.cost,
    feeRate: pricing.feeRate,
    shipping: pricing.shipping,
    targetMargin,
  });

  const reprice = Number.isFinite(targetMargin)
    ? suggestReprice({
        currentPrice: Number(currentPrice),
        floorMargin: targetMargin,
        cost: pricing.cost,
        feeRate: pricing.feeRate,
        shipping: pricing.shipping,
        buyBoxStatus: buyBox?.status ?? null,
        winnerPrice: buyBox?.winnerPrice ?? null,
        priceToWin: buyBox?.priceToWin ?? null,
        mlSuggested: suggestion?.price ?? null,
      })
    : null;

  const apply = useMutation({
    mutationFn: async (input: { price: number; source: string }) => {
      return unwrap(
        await api.listings({ id: listingId }).patch({
          price: input.price,
          source: input.source,
        }),
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["listings"] });
      toast.add({ type: "success", title: "Preço atualizado" });
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title: "Não foi possível alterar o preço",
        description:
          error instanceof Error ? error.message : "Tente novamente.",
      });
    },
  });

  const hint =
    result.reason === "missing_cost"
      ? "Cadastre o custo do SKU em Estoque para sugerir o preço."
      : result.reason === "impossible_margin"
        ? "Essa margem não cabe depois da taxa de venda."
        : result.reason === "invalid_margin"
          ? "Informe uma margem entre 1% e 99%."
          : pricing.source === "official"
            ? "Taxa da tabela oficial do Mercado Livre — este anúncio ainda não tem venda recente."
            : pricing.sampleOrders === 0
              ? "Sem venda recente — taxa e frete entram como zero até importar pedidos."
              : `Taxa das suas vendas — média das últimas ${pricing.sampleOrders} venda${pricing.sampleOrders === 1 ? "" : "s"} (30 dias).`;

  const samePrice =
    result.price != null && Number(result.price) === Number(currentPrice);
  const chasePrice = reprice?.price;
  const chaseSame =
    chasePrice != null && Number(chasePrice) === Number(currentPrice);
  const chaseIsTarget =
    chasePrice != null &&
    result.price != null &&
    Number(chasePrice) === Number(result.price);
  const chaseHint =
    reprice?.reason === "follow_winner"
      ? "Acompanha o vencedor do card, sem furar o piso."
      : reprice?.reason === "follow_suggested"
        ? "Referência oficial do Mercado Livre, sem furar o piso."
        : reprice?.reason === "floor"
          ? "O vencedor está abaixo do piso — este é o mínimo para a margem."
          : reprice?.reason === "raise_floor"
            ? "Preço atual abaixo do piso desta margem."
            : null;

  return (
    <Card className="animate-rise">
      <CardHeader>
        <CardTitle>Preço-alvo</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 p-4">
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
          <Field className="col-span-2 sm:col-span-1">
            <FieldLabel>Margem desejada</FieldLabel>
            <div className="relative">
              <Input
                value={marginPct}
                inputMode="decimal"
                onChange={(event) => setMarginPct(event.target.value)}
                className="pr-8"
              />
              <span className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-2xs text-ink-muted">
                %
              </span>
            </div>
          </Field>
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="text-2xs font-medium text-ink-muted">
              Preço sugerido
            </span>
            <span className="font-mono text-[0.9375rem] font-semibold text-ink">
              {result.price ? formatBRL(result.price) : "—"}
            </span>
          </div>
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="text-2xs font-medium text-ink-muted">Custo</span>
            <span className="font-mono text-[0.8125rem] text-ink">
              {pricing.cost ? formatBRL(pricing.cost) : "—"}
            </span>
          </div>
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="text-2xs font-medium text-ink-muted">
              Taxa + frete
            </span>
            <span className="font-mono text-[0.8125rem] text-ink">
              {pricing.feeRate != null
                ? formatPercent(pricing.feeRate)
                : "—"}
              {pricing.shipping != null
                ? ` · ${formatBRL(pricing.shipping)}`
                : ""}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-2xs text-ink-muted">{hint}</p>
          <Button
            size="sm"
            disabled={!canWrite || result.price == null || samePrice}
            loading={apply.isPending}
            onClick={() => {
              if (result.price != null) {
                apply.mutate({
                  price: Number(result.price),
                  source: "target",
                });
              }
            }}
          >
            Aplicar este preço
          </Button>
        </div>
        {chasePrice && chaseHint && !chaseSame ? (
          <div className="flex flex-col gap-2 border-t border-line pt-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-2xs font-medium uppercase tracking-wider text-ink-muted">
                Acompanhar o card
              </p>
              <p
                className={cn(
                  "mt-0.5 font-mono text-xl font-medium tracking-tight",
                  reprice?.guarded ? "text-danger" : "text-ink",
                )}
              >
                {formatBRL(currentPrice)}
                <span className="mx-1.5 text-ink-faint">→</span>
                {formatBRL(chasePrice)}
              </p>
              <p className="mt-0.5 text-2xs text-ink-muted">{chaseHint}</p>
            </div>
            {!chaseIsTarget ? (
              <Button
                size="sm"
                disabled={!canWrite}
                loading={apply.isPending}
                onClick={() =>
                  apply.mutate({
                    price: Number(chasePrice),
                    source: "reprice",
                  })
                }
              >
                Aplicar {formatBRL(chasePrice)}
              </Button>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function ListingPriceEditor({
  listingId,
  price,
  canWrite,
}: {
  listingId: string;
  price: string;
  canWrite: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(price);
  const toast = useToast();
  const queryClient = useQueryClient();
  const parsed = parseBRL(draft);

  const save = useMutation({
    mutationFn: async () => {
      if (parsed == null) throw new Error("Informe um preço válido");
      return unwrap(
        await api.listings({ id: listingId }).patch({ price: parsed }),
      );
    },
    onSuccess: () => {
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey: ["listings"] });
      toast.add({ type: "success", title: "Preço atualizado" });
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title: "Não foi possível alterar o preço",
        description:
          error instanceof Error ? error.message : "Tente novamente.",
      });
    },
  });

  if (!editing) {
    return (
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-2xs text-ink-muted">Preço</span>
        <div className="flex items-center gap-1.5">
          <span className="text-[0.8125rem] font-semibold text-ink">
            {formatBRL(price)}
          </span>
          {canWrite && (
            <Button
              variant="ghost"
              size="icon"
              className="size-6"
              onClick={() => {
                setDraft(price);
                setEditing(true);
              }}
            >
              <Pencil />
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <form
      className="col-span-2 flex flex-col gap-2 sm:col-span-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (parsed != null) save.mutate();
      }}
    >
      <Field>
        <FieldLabel>Preço</FieldLabel>
        <Input
          value={draft}
          inputMode="decimal"
          autoFocus
          onChange={(event) => setDraft(event.target.value)}
        />
      </Field>
      <div className="flex items-center justify-end gap-1.5">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setEditing(false)}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          size="sm"
          loading={save.isPending}
          disabled={parsed == null}
        >
          Salvar
        </Button>
      </div>
    </form>
  );
}
